/**
 * Tote IDP Design System — token reference for the deploy-v3 plugin.
 *
 * Source of truth: docs/design-system/mockup-template.html and
 * docs/design-system/README.md. These tokens are the *non-negotiable*
 * contract from CLAUDE.md: every plugin uses these values, never
 * hardcodes hex.
 *
 * Match: design-system v1.x ($PR #72) — keep in sync.
 */

export const tokens = {
  // ── Surfaces ────────────────────────────────────────────────
  bg: {
    page: '#FAFAFF',
    surface: '#FFFFFF',
    elevated: '#F8F8F8',
    rowHover: '#F4F4F4',
    rowDraft: '#EFEFFD',
    // Dark navy surface for full-page header bars (e.g. the approval-inbox
    // hero, prototype A2). Mirrors the deep-indigo hero family; kept as a
    // token so no page hardcodes the hex (Rule 1).
    inverse: '#1a1d2e',
  },

  // ── Brand ───────────────────────────────────────────────────
  brand: {
    primary: '#625DEC',
    primaryHover: '#4F4CC0',
    primaryLight: '#EFEFFD',
    primarySubtle: '#FAFAFF',
  },

  // ── Ink (text) ──────────────────────────────────────────────
  ink: {
    base: '#232323',
    secondary: '#656565',
    tertiary: '#B9B9B9', // decorative only — never body text
    disabled: '#B9B9B9',
    inverse: '#FFFFFF',
  },

  // ── Borders ─────────────────────────────────────────────────
  //
  // `base` and `strong` are DECORATIVE: card edges, table rules, dividers.
  // Neither clears WCAG 1.4.11's 3:1 (base is 1.12–1.24:1, strong 1.78–1.96:1),
  // which is fine — a divider is not a UI component and has no contrast floor.
  //
  // `interactive` is the ONLY border permitted to draw the boundary of a
  // control (button / input / select / checkbox / tab). Reach for it whenever
  // the border is the thing telling a user "this is operable" — i.e. whenever
  // the control's own fill does not distinguish it from the surface behind it.
  // #8A8A8A clears 3:1 on all four surfaces (3.45 / 3.32 / 3.25 / 3.14).
  // Do not lighten it: #8D8D8D is the lightest value that still passes, and
  // only by 0.02 on rowHover.
  border: {
    base: '#E7E7E7',
    strong: '#B9B9B9',
    interactive: '#8A8A8A',
  },

  // ── Status semantics ────────────────────────────────────────
  status: {
    success: { base: '#2B7F04', light: '#E9FFDF', ink: '#1C5400' },
    warning: { base: '#E65100', light: '#FFF3E0', ink: '#7A3C00' },
    error: { base: '#980008', light: '#FEF7F7', ink: '#5C0000' },
    info: { base: '#005394', light: '#E6F4FF', ink: '#003F70' },
  },

  // ── Environment palette (per-env chip + accents) ────────────
  env: {
    dev: { bg: '#E0F2FE', ink: '#075985' },
    qa: { bg: '#FEF3C7', ink: '#92400E' },
    stg: { bg: '#EDE9FE', ink: '#5B21B6' },
    prod: { bg: '#FEE2E2', ink: '#991B1B' },
  },

  // ── Shape ───────────────────────────────────────────────────
  radius: {
    sm: 6,
    md: 8, // canonical default
    lg: 12,
    full: 9999,
  },

  // ── Elevation ───────────────────────────────────────────────
  shadow: {
    sm: '0 1px 2px rgba(35,35,35,0.04)',
    md: '0 4px 12px rgba(35,35,35,0.06), 0 1px 3px rgba(35,35,35,0.04)',
    lg: '0 12px 32px rgba(35,35,35,0.12)',
    modal: '0 24px 48px rgba(35,35,35,0.18)',
  },

  // ── Typography ──────────────────────────────────────────────
  font: {
    sans: '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    size: {
      xs: 11,
      sm: 12.5,
      base: 14,
      md: 15,
      lg: 18,
      xl: 22,
    },
    weight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // ── Spacing scale (4-pt) ────────────────────────────────────
  space: {
    xs: 4,
    sm: 8,
    md: 12,
    base: 16,
    lg: 24,
    xl: 32,
    '2xl': 48,
  },
} as const;

/** Dark values share the light token shape so every deploy surface has one source. */
export const darkTokens = {
  ...tokens,
  bg: {
    page: '#303030',
    surface: '#383838',
    elevated: '#424242',
    rowHover: '#4A4A4A',
    rowDraft: '#373653',
    inverse: '#A9A6FF',
  },
  brand: {
    primary: '#C4C1FF',
    primaryHover: '#DEDcff',
    primaryLight: '#373653',
    primarySubtle: '#323144',
  },
  ink: {
    base: '#F4F4F6',
    secondary: '#CECED3',
    tertiary: '#AAAAB0',
    disabled: '#AAAAB0',
    inverse: '#1A1D2E',
  },
  border: { base: '#55555B', strong: '#767680', interactive: '#A7A7B0' },
  status: {
    success: { base: '#8BCE74', light: '#243A2B', ink: '#B8E7A7' },
    warning: { base: '#FFAF70', light: '#493523', ink: '#FFD0A3' },
    error: { base: '#F28F98', light: '#452B31', ink: '#FFC0C5' },
    info: { base: '#83C4F4', light: '#263B4C', ink: '#B5DCFA' },
  },
  env: {
    dev: { bg: '#243B4A', ink: '#A9D8F4' },
    qa: { bg: '#473B24', ink: '#FFE0A0' },
    stg: { bg: '#39304F', ink: '#D5C3FF' },
    prod: { bg: '#493034', ink: '#FFC1C1' },
  },
  shadow: {
    sm: '0 1px 2px rgba(0,0,0,0.2)',
    md: '0 4px 12px rgba(0,0,0,0.24)',
    lg: '0 12px 32px rgba(0,0,0,0.35)',
    modal: '0 24px 48px rgba(0,0,0,0.48)',
  },
} as const;

const colorGroups = [
  'bg',
  'brand',
  'ink',
  'border',
  'status',
  'env',
  'shadow',
] as const;
interface TokenTree {
  [key: string]: string | TokenTree;
}
function variableTree(value: TokenTree, path: string[] = []): TokenTree {
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => {
      const next = [...path, key];
      return [
        key,
        typeof entry === 'string'
          ? `var(--dv3-${next.join('-')})`
          : variableTree(entry, next),
      ];
    }),
  );
}

/** Color references used by component styles; the active theme supplies values. */
export const themedTokens = {
  ...tokens,
  ...Object.fromEntries(
    colorGroups.map(group => [
      group,
      variableTree(tokens[group] as TokenTree, [group]),
    ]),
  ),
} as unknown as typeof tokens;

export function applyDeployTheme(dark: boolean): () => void {
  const root = document.documentElement;
  const palette = dark ? darkTokens : tokens;
  const previous = new Map<string, string>();
  const set = (name: string, value: string) => {
    previous.set(name, root.style.getPropertyValue(name));
    root.style.setProperty(name, value);
  };
  const assign = (value: TokenTree, path: string[] = []) => {
    Object.entries(value).forEach(([key, entry]) => {
      const next = [...path, key];
      if (typeof entry === 'string') set(`--dv3-${next.join('-')}`, entry);
      else assign(entry, next);
    });
  };
  colorGroups.forEach(group => assign(palette[group] as TokenTree, [group]));

  // @platform/ui atoms use the shared CSS token channel. Keep both channels
  // aligned while this page is mounted, including dialog portals under body.
  const bridge: Record<string, string> = {
    '--bg-page': palette.bg.page,
    '--bg-surface': palette.bg.surface,
    '--bg-elevated': palette.bg.elevated,
    '--bg-row-hover': palette.bg.rowHover,
    '--bg-row-draft': palette.bg.rowDraft,
    '--brand-primary': palette.brand.primary,
    '--brand-primary-hover': palette.brand.primaryHover,
    '--brand-primary-light': palette.brand.primaryLight,
    '--brand-primary-subtle': palette.brand.primarySubtle,
    '--ink': palette.ink.base,
    '--ink-secondary': palette.ink.secondary,
    '--ink-tertiary': palette.ink.tertiary,
    '--ink-disabled': palette.ink.disabled,
    '--ink-inverse': palette.ink.inverse,
    '--border': palette.border.base,
    '--border-strong': palette.border.strong,
    '--border-interactive': palette.border.interactive,
  };
  (['success', 'warning', 'error', 'info'] as const).forEach(status => {
    bridge[`--${status}`] = palette.status[status].base;
    bridge[`--${status}-dark`] = palette.status[status].base;
    bridge[`--${status}-light`] = palette.status[status].light;
    bridge[`--${status}-ink`] = palette.status[status].ink;
  });
  (['dev', 'qa', 'stg', 'prod'] as const).forEach(env => {
    bridge[`--env-${env}-bg`] = palette.env[env].bg;
    bridge[`--env-${env}-ink`] = palette.env[env].ink;
  });
  Object.entries(bridge).forEach(([name, value]) => set(name, value));
  return () =>
    previous.forEach((value, name) => {
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    });
}

export type Env = 'dev' | 'qa' | 'stg' | 'prod';

/** Environment names are identity labels; status colours are reserved for state. */
export function envPalette(_env: string): { bg: string; ink: string } {
  return { bg: themedTokens.bg.elevated, ink: themedTokens.ink.secondary };
}
